/**
 * ListPicker.tsx - List selection component
 * 
 * CHANGE HISTORY:
 * - 251130_1628 danielbeer: Created list picker component for ListManager
 * 
 * Displays available lists with filtering and selection.
 */

import React, { useState, useMemo } from 'react';
import type { ListData } from '../types';

export interface ListPickerProps {
  lists: ListData[];
  selectedListId: string | null;
  onSelectList: (listId: string) => void;
  loading?: boolean;
}

/**
 * List picker component with filtering
 * 
 * @param props - Component props
 * @returns List picker UI
 * 
 * @example
 * <ListPicker
 *   lists={availableLists}
 *   selectedListId={selectedListId}
 *   onSelectList={setSelectedListId}
 * />
 */
export const ListPicker: React.FC<ListPickerProps> = ({
  lists,
  selectedListId,
  onSelectList,
  loading = false
}) => {
  const [filterText, setFilterText] = useState('');
  
  // Filter lists based on search text
  const filteredLists = useMemo(() => {
    if (!filterText.trim()) return lists;
    
    const searchLower = filterText.toLowerCase();
    return lists.filter(list =>
      list.name.toLowerCase().includes(searchLower) ||
      list.description?.toLowerCase().includes(searchLower) ||
      list.id.toLowerCase().includes(searchLower)
    );
  }, [lists, filterText]);
  
  return (
    <div className="picker-container" style={{ height: '100%' }}>
      {/* Header with inline search */}
      <div className="picker-header">
        <h3 className="picker-title">
          📋 Lists {filterText ? `(${filteredLists.length}/${lists.length})` : `(${lists.length})`}
        </h3>
        <div style={{ maxWidth: '120px', marginLeft: 'auto', paddingRight: '8px' }}>
          <div className="picker-search-wrapper">
            <input
              type="text"
              placeholder="Filter..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="picker-search"
            />
            {filterText && (
              <button
                onClick={() => setFilterText('')}
                className="picker-search-clear"
                title="Clear filter"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* List of Lists */}
      <div className="picker-list">
        {filteredLists.length === 0 ? (
          <div className="picker-empty">
            {filterText ? 'No lists match your filter' : 'No lists available'}
          </div>
        ) : (
          filteredLists.map(list => (
            <button
              key={list.id}
              onClick={() => onSelectList(list.id)}
              disabled={loading}
              title={`${list.description}\n\nID: ${list.id}\nType: ${list.type === 'dynamic' ? 'Dynamic (Query-based)' : 'Static (Manual)'}\nItems: ${Array.isArray(list.children) ? list.children.length : 0}`}
              className={`picker-item ${selectedListId === list.id ? 'active' : ''}`}
            >
              {/* Icon */}
              <span className="picker-item-icon">
                {list.icon || (list.type === 'dynamic' ? '🔄' : '📋')}
              </span>
              
              {/* Name */}
              <span className="picker-item-label">
                {list.shortName || list.name}
              </span>
              
              {/* Item Count Badge */}
              <span className="picker-item-badge">
                {Array.isArray(list.children) ? list.children.length : 0}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default ListPicker;
